/**
 * Canva Apps SDK integration for photobook editor
 * Handles proper asset upload and drag-to-editor functionality
 */

import React from "react";
import { ui } from "@canva/design";
import { upload } from "@canva/asset";
import { features } from "@canva/platform";

// Types
export type DragEvt = MouseEvent | PointerEvent | TouchEvent | undefined;

export interface Photo {
  id: string;
  name: string;
  file?: File | Blob;
  publicUrl?: string;
  cdnUrl?: string;
  previewUrl?: string;
  previewSize?: { width: number; height: number };
  mimeType?: string;
  dimensions?: { w: number; h: number };
  canvaAssetId?: string; // Canva asset reference
  canvaAssetRef?: string; // Canva asset ref string
}

export interface PhotoDragData extends Photo {
  // Legacy compatibility
  resolveImageRef?: () => Promise<string>;
}

export interface CanvaAsset {
  assetId: string;
  assetRef: string;
  thumbUrl?: string;
}

export interface FrameBox {
  id: string;
  rect: DOMRect;
  z: number;
  element?: HTMLElement;
}

export interface DragState {
  active: boolean;
  photo: PhotoDragData | null;
  startPoint: { x: number; y: number } | null;
  currentPoint: { x: number; y: number } | null;
  hoveredFrame: FrameBox | null;
  pointerId?: number;
}

// Global drag state
let dragState: DragState = {
  active: false,
  photo: null,
  startPoint: null,
  currentPoint: null,
  hoveredFrame: null
};

// Frame detection system
let framesIndex: FrameBox[] = [];
let frameUpdateScheduled = false;

// Safe event utilities with type guards
export function safePreventDefault(evt: any): void {
  if (evt && typeof evt.preventDefault === "function") {
    try {
      evt.preventDefault();
    } catch (e) {
      console.warn('[drag] preventDefault failed:', e);
    }
  }
}

export function safeStopPropagation(evt: any): void {
  if (evt && typeof evt.stopPropagation === "function") {
    try {
      evt.stopPropagation();
    } catch (e) {
      console.warn('[drag] stopPropagation failed:', e);
    }
  }
}

export function getXY(evtOrPoint: any): { x: number; y: number } {
  if (!evtOrPoint) return { x: 0, y: 0 };

  // If it's already a point object
  if (typeof evtOrPoint.x === "number" && typeof evtOrPoint.y === "number") {
    return { x: evtOrPoint.x, y: evtOrPoint.y };
  }

  // If it's a mouse/pointer event
  if (typeof evtOrPoint.clientX === "number" && typeof evtOrPoint.clientY === "number") {
    return { x: evtOrPoint.clientX, y: evtOrPoint.clientY };
  }

  // If it's a touch event
  const touch = evtOrPoint.changedTouches?.[0] ?? evtOrPoint.touches?.[0];
  if (touch && typeof touch.clientX === "number") {
    return { x: touch.clientX, y: touch.clientY };
  }

  return { x: 0, y: 0 };
}

// Frame detection
export function pointInRect(point: { x: number; y: number }, rect: DOMRect): boolean {
  return point.x >= rect.left && 
         point.x <= rect.right && 
         point.y >= rect.top && 
         point.y <= rect.bottom;
}

export function computeFrameRects(): FrameBox[] {
  const frames: FrameBox[] = [];
  
  // Look for Canva frames in the design area
  // This is a simplified implementation - in practice, you'd need to
  // integrate with Canva's frame detection API or DOM structure
  const frameElements = document.querySelectorAll('[data-frame-id], .frame-element, .drop-target');
  
  frameElements.forEach((element, index) => {
    const rect = element.getBoundingClientRect();
    const id = element.getAttribute('data-frame-id') || `frame-${index}`;
    const zIndex = parseInt(getComputedStyle(element).zIndex || '0', 10);
    
    frames.push({
      id,
      rect,
      z: zIndex,
      element: element as HTMLElement
    });
  });
  
  return frames.sort((a, b) => b.z - a.z); // Sort by z-index, highest first
}

export function updateFramesIndex() {
  if (frameUpdateScheduled) return;
  
  frameUpdateScheduled = true;
  requestAnimationFrame(() => {
    framesIndex = computeFrameRects();
    frameUpdateScheduled = false;
    console.log('[drag] Updated frames index:', framesIndex.length, 'frames');
  });
}

export function findHoveredFrame(point: { x: number; y: number }): FrameBox | null {
  return framesIndex.find(frame => pointInRect(point, frame.rect)) || null;
}

// Visual feedback
export function highlightFrame(frame: FrameBox | null) {
  // Remove previous highlights
  document.querySelectorAll('.frame-highlight').forEach(el => {
    el.classList.remove('frame-highlight');
  });
  
  if (frame?.element) {
    frame.element.classList.add('frame-highlight');
    console.log('[drag] Highlighting frame:', frame.id);
  }
}

// Check if we're in Canva environment
export function isCanvaEnvironment(): boolean {
  try {
    return typeof window !== 'undefined' &&
           (window as any).isCanvaEnvironment === true;
  } catch {
    return false;
  }
}

// Health check for external URLs
async function healthCheckUrl(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

// Create Canva image asset using the official SDK
export async function createCanvaImageAsset(input: {
  file?: File | Blob;
  publicUrl?: string;
  title?: string;
  width?: number;
  height?: number;
}): Promise<CanvaAsset> {
  console.log('[asset] Creating Canva asset:', {
    hasFile: !!input.file,
    publicUrl: input.publicUrl,
    title: input.title,
    dimensions: input.width && input.height ? `${input.width}x${input.height}` : 'unknown'
  });

  try {
    let uploadUrl: string;
    let mimeType: string;
    let thumbnailUrl: string;

    if (input.file) {
      // Preferred: Upload from File/Blob
      console.log('[asset] Using File/Blob for upload');

      // Create data URL for upload
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(input.file!);
      });

      uploadUrl = dataUrl;
      thumbnailUrl = dataUrl;
      mimeType = input.file.type || 'image/jpeg';
    } else if (input.publicUrl) {
      // Fallback: Use public URL after health check
      console.log('[asset] Using public URL for upload');

      const isHealthy = await healthCheckUrl(input.publicUrl);
      if (!isHealthy) {
        throw new Error(`Public URL health check failed: ${input.publicUrl}`);
      }

      uploadUrl = input.publicUrl;
      thumbnailUrl = input.publicUrl;
      mimeType = 'image/jpeg'; // Default, could be improved with HEAD request
    } else {
      throw new Error('Either file or publicUrl must be provided');
    }

    // Validate and normalize MIME type for Canva
    const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    let validMimeType = mimeType;

    if (!supportedTypes.includes(mimeType)) {
      // Try to detect from URL extension
      const urlLower = uploadUrl.toLowerCase();
      if (urlLower.includes('.png') || urlLower.includes('png')) {
        validMimeType = 'image/png';
      } else if (urlLower.includes('.gif') || urlLower.includes('gif')) {
        validMimeType = 'image/gif';
      } else if (urlLower.includes('.webp') || urlLower.includes('webp')) {
        validMimeType = 'image/webp';
      } else {
        validMimeType = 'image/jpeg'; // Default fallback
      }
      console.log(`[asset] Fixed MIME type: ${mimeType} -> ${validMimeType}`);
    }

    // Upload to Canva using official SDK
    const asset = await upload({
      type: "image",
      url: uploadUrl,
      mimeType: validMimeType as any,
      thumbnailUrl: thumbnailUrl,
      aiDisclosure: "none",
      width: input.width,
      height: input.height
    });

    console.log('[asset] Canva asset created successfully:', {
      assetRef: asset.ref,
      title: input.title
    });

    return {
      assetId: asset.ref, // Legacy compatibility
      assetRef: asset.ref,
      thumbUrl: thumbnailUrl
    };
  } catch (error) {
    console.error('[asset] Failed to create Canva asset:', error);
    throw error;
  }
}

// Start Canva image drag using official SDK
export async function startCanvaImageDrag(event: React.DragEvent<HTMLElement>, photo: Photo): Promise<boolean> {
  try {
    console.log('[drag] Starting Canva image drag:', {
      photoId: photo.id,
      hasCanvaAsset: !!photo.canvaAssetRef,
      isCanvaEnvironment: isCanvaEnvironment()
    });

    if (!isCanvaEnvironment()) {
      console.warn('[drag] Not in Canva environment, SDK drag not available');
      return false;
    }

    // Ensure we have a Canva asset
    let assetRef = photo.canvaAssetRef;
    if (!assetRef) {
      console.log('[drag] Creating Canva asset for drag...');
      const asset = await createCanvaImageAsset({
        file: photo.file,
        publicUrl: photo.publicUrl || photo.cdnUrl,
        title: photo.name,
        width: photo.dimensions?.w,
        height: photo.dimensions?.h
      });
      assetRef = asset.assetRef;

      // Update photo with asset reference
      photo.canvaAssetRef = assetRef;
      photo.canvaAssetId = asset.assetId;
    }

    // Use official Canva SDK drag API with proper payload
    // According to docs: resolveImageRef should return Promise<{ ref: ImageRef }>
    const dragPayload = {
      type: "image" as const,
      resolveImageRef: () => {
        // Return existing asset reference wrapped in Promise
        return Promise.resolve({ ref: assetRef! });
      },
      previewUrl: photo.previewUrl || photo.cdnUrl || photo.publicUrl,
      previewSize: photo.previewSize || photo.dimensions ? {
        width: photo.dimensions?.w || 160,
        height: photo.dimensions?.h || 120
      } : { width: 160, height: 120 },
      fullSize: photo.dimensions ? {
        width: photo.dimensions.w,
        height: photo.dimensions.h
      } : undefined
    };

    // Call the appropriate SDK method based on design type
    // According to official API: ui.startDragToPoint(event, dragData)
    console.log('[drag] About to call Canva SDK with:', {
      hasStartDragToPoint: features.isSupported(ui.startDragToPoint),
      hasStartDragToCursor: features.isSupported(ui.startDragToCursor),
      eventType: event.type,
      assetRef,
      dragPayloadType: dragPayload.type
    });

    if (features.isSupported(ui.startDragToPoint)) {
      console.log('[drag] startDragToPoint', photo.name);
      ui.startDragToPoint(event, dragPayload);
      console.log('[drag] Called ui.startDragToPoint with asset:', assetRef);
      return true;
    } else if (features.isSupported(ui.startDragToCursor)) {
      console.log('[drag] startDragToCursor', photo.name);
      ui.startDragToCursor(event, dragPayload);
      console.log('[drag] Called ui.startDragToCursor with asset:', assetRef);
      return true;
    } else {
      console.warn('[drag] No supported Canva drag API available');
      return false;
    }
  } catch (error) {
    console.error('[drag] Canva image drag failed:', error);
    return false;
  }
}

// Handle pointer down events for drag initiation
export function onPhotoPointerDown(evt: PointerEvent, photo: Photo): void {
  console.log('[drag] Photo pointer down:', {
    photoId: photo.id,
    hasCanvaAsset: !!photo.canvaAssetRef,
    isCanvaEnvironment: isCanvaEnvironment()
  });

  // Safe event handling
  safePreventDefault(evt);
  safeStopPropagation(evt);

  // In Canva environment, we don't use HTML5 drag - just prepare for click
  if (isCanvaEnvironment()) {
    console.log('[drag] In Canva environment - drag will be handled by onDragStart');
    return;
  }

  // For standalone mode, update drag state for visual feedback
  const point = getXY(evt);
  dragState = {
    active: true,
    photo,
    startPoint: point,
    currentPoint: point,
    hoveredFrame: null,
    pointerId: evt.pointerId
  };

  updateFramesIndex();
  startInternalDrag(evt, photo);
}

// Handle drag start events (HTML5 drag API)
export async function onPhotoDragStart(event: React.DragEvent<HTMLElement>, photo: Photo): Promise<void> {
  console.log('[drag] Photo drag start:', {
    photoId: photo.id,
    isCanvaEnvironment: isCanvaEnvironment(),
    hasCanvaAsset: !!photo.canvaAssetRef,
    eventType: event.type,
    eventConstructor: event.constructor.name
  });

  if (isCanvaEnvironment()) {
    // In Canva, use the official SDK
    const success = await startCanvaImageDrag(event, photo);
    if (!success) {
      console.error('[drag] Failed to start Canva image drag');
      safePreventDefault(event);
    }
  } else {
    // In standalone mode, prevent default HTML5 drag
    safePreventDefault(event);
    console.log('[drag] Prevented HTML5 drag in standalone mode');
  }
}

// Legacy compatibility functions
export function startPhotoDrag(evtOrPoint: DragEvt | {x:number;y:number}, photo: Photo): void {
  if (evtOrPoint && typeof (evtOrPoint as any).type === 'string') {
    const evt = evtOrPoint as PointerEvent;
    onPhotoPointerDown(evt, photo);
  }
}

export function startDrag(evt: DragEvt, photo: PhotoDragData): void {
  startPhotoDrag(evt, photo);
}

// Internal drag system for frame detection and visual feedback
function startInternalDrag(evtOrPoint: DragEvt | {x:number;y:number}, photo: Photo): void {
  const evt = evtOrPoint as DragEvt;
  const target = evt?.target as HTMLElement;

  // Set pointer capture if available (for proper pointer events)
  if (target && 'setPointerCapture' in target && dragState.pointerId !== undefined) {
    try {
      target.setPointerCapture(dragState.pointerId);
      console.log('[drag] Set pointer capture for pointerId:', dragState.pointerId);
    } catch (e) {
      console.warn('[drag] setPointerCapture failed:', e);
    }
  }

  // Attach move and end listeners with proper event types
  const moveHandler = (e: Event) => handleDragMove(e as DragEvt);
  const endHandler = (e: Event) => handleDragEnd(e as DragEvt);

  // Prefer pointer events, fallback to mouse/touch
  const eventType = evt?.type || 'pointer';

  if (eventType.startsWith('pointer')) {
    window.addEventListener('pointermove', moveHandler, { passive: false });
    window.addEventListener('pointerup', endHandler, { once: true });
    window.addEventListener('pointercancel', endHandler, { once: true });
  } else if (eventType.startsWith('touch')) {
    window.addEventListener('touchmove', moveHandler, { passive: false });
    window.addEventListener('touchend', endHandler, { once: true });
    window.addEventListener('touchcancel', endHandler, { once: true });
  } else {
    // Mouse events fallback
    window.addEventListener('mousemove', moveHandler, { passive: false });
    window.addEventListener('mouseup', endHandler, { once: true });
  }

  // Store cleanup function
  (dragState as any).cleanup = () => {
    window.removeEventListener('pointermove', moveHandler);
    window.removeEventListener('touchmove', moveHandler);
    window.removeEventListener('mousemove', moveHandler);
    window.removeEventListener('pointerup', endHandler);
    window.removeEventListener('pointercancel', endHandler);
    window.removeEventListener('touchend', endHandler);
    window.removeEventListener('touchcancel', endHandler);
    window.removeEventListener('mouseup', endHandler);
  };
}

function handleDragMove(evt: DragEvt): void {
  if (!dragState.active) return;

  safePreventDefault(evt);
  const point = getXY(evt);
  dragState.currentPoint = point;

  // Find hovered frame for visual feedback
  const newHoveredFrame = findHoveredFrame(point);

  if (newHoveredFrame !== dragState.hoveredFrame) {
    console.log('[drag] Hover change:', { hoveredFrameId: newHoveredFrame?.id || null });
    dragState.hoveredFrame = newHoveredFrame;
    highlightFrame(newHoveredFrame);
  }
}

function handleDragEnd(evt: DragEvt): void {
  const acceptedBySDK = isCanvaEnvironment() && dragState.hoveredFrame;

  console.log('[drag] Drag ended:', {
    acceptedBySDK,
    hoveredFrameId: dragState.hoveredFrame?.id || null,
    isCanvaEnvironment: isCanvaEnvironment()
  });

  if (dragState.hoveredFrame && dragState.photo) {
    console.log('[drag] Would drop photo into frame:', dragState.hoveredFrame.id);
    // In Canva environment, the actual drop is handled by the SDK
    // This is just for visual feedback and logging
  }

  // Cleanup visual feedback
  highlightFrame(null);

  if ((dragState as any).cleanup) {
    (dragState as any).cleanup();
  }

  // Reset state
  dragState = {
    active: false,
    photo: null,
    startPoint: null,
    currentPoint: null,
    hoveredFrame: null
  };
}

// Initialize frame detection
export function initializeDragSystem(): void {
  console.log('[drag] Initializing drag system');
  
  // Update frames on resize
  window.addEventListener('resize', updateFramesIndex);
  
  // Update frames on DOM changes (throttled)
  const observer = new MutationObserver(() => {
    updateFramesIndex();
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-frame-id', 'class']
  });
  
  // Initial frame detection
  updateFramesIndex();
}

// Export drag state for debugging
export function getDragState(): DragState {
  return { ...dragState };
}

export function getFramesIndex(): FrameBox[] {
  return [...framesIndex];
}

// Expose debug functions globally for testing
if (typeof window !== 'undefined') {
  (window as any).getDragState = getDragState;
  (window as any).getFramesIndex = getFramesIndex;
  (window as any).updateFramesIndex = updateFramesIndex;
  (window as any).computeFrameRects = computeFrameRects;
}
